import axios from '@/libs/axios';

import { requestRegistry, buildRequestSignal } from '@/libs/requestRegistry';
import { useAuthStore } from '@/store/authStore';
import { useSettingStore } from '@/store/settingStore';
import { storeToRefs } from 'pinia';

const getAuthStore = () => useAuthStore();

const apiService: API.ApiService = {
  apiRequest(model: API.RequestModel) {
    return new Promise((resolve, reject) => {
      // Bootstrap calls (permissions, module versions, brand list) must never be
      // cancelled by page navigation — they aren't scoped to any single page.
      if (model?.bypassNavigationCancel) {
        axios(apiUtilities.buildPayload(model, model?.signal))
          .then((res) => resolve(res.data))
          .catch((err) => {
            if (err?.code === 'ERR_CANCELED') return;
            return reject(err.response || err);
          });
        return;
      }
      const { key, signal } = buildRequestSignal(model?.signal);
      axios(apiUtilities.buildPayload(model, signal))
        .then((res) => {
          resolve(res.data);
        })
        .catch((err) => {
          if (err?.code === 'ERR_CANCELED') return;
          const error = err.response || err;
          return reject(error);
        })
        .finally(() => {
          requestRegistry.deregister(key);
        });
    });
  },
  apiFormRequest(model: API.RequestModel) {
    return new Promise((resolve, reject) => {
      const { key, signal } = buildRequestSignal(model?.signal);
      axios(
        apiUtilities.buildPayload(
          {
            ...model,
            payload: apiUtilities.calculateFormDataObject(model.payload) || {},
          },
          signal,
        ),
      )
        .then((res) => {
          resolve(res.data);
        })
        .catch((err) => {
          if (err?.code === 'ERR_CANCELED') return;
          const error = err.response || err;
          return reject(error);
        })
        .finally(() => {
          requestRegistry.deregister(key);
        });
    });
  },
};

const apiUtilities = {
  buildPayload(model: API.RequestModel, signal?: AbortSignal) {
    const settingStore = useSettingStore();
    const { currentLocale } = storeToRefs(settingStore);
    const accessToken = getAuthStore().access_token;
    return {
      method: model.method.toLowerCase(),
      url: this.pathParamsEndpoint(model.endpoint, model.pathParams),
      headers: {
        Authorization: `Bearer ${accessToken as string}`,
        ['X-Request-Id']: model?.xRequestId || '',
        ...(model.acceptLanguage
          ? { ['Accept-Language']: model.acceptLanguage == 'th' ? 'th-TH' : model.acceptLanguage }
          : { ['Accept-Language']: currentLocale.value == 'th' ? 'th-TH' : currentLocale.value }),
      },
      [model.method == 'GET' ? 'params' : 'data']: model.payload,
      responseType: model?.responseType,
      signal: signal ?? model?.signal,
    };
  },
  pathParamsEndpoint(endpoint: string, pathParams?: Record<string, string | number | boolean | null>) {
    if (!pathParams) return endpoint;
    endpoint = endpoint.replace(/:([a-zA-Z_][a-zA-Z0-9_]*)/g, (_, key: string) => {
      if (key in pathParams && pathParams[key] != null) return encodeURIComponent(pathParams[key] as string | number | boolean);
      console.error(`Missing value for path param ':${key}'`);
      throw new Error(`Missing value for path param ':${key}'`);
    });

    return endpoint;
  },
  calculateFormDataObject(ObjectData?: Record<string, any>, Path?: Array<any>, FormDataObj?: FormData): FormData | null {
    let FormDataBase = FormDataObj || new FormData();

    if (ObjectData instanceof File) {
      const PathObj = Path != null ? JSON.parse(JSON.stringify(Path)) : [];
      FormDataBase.append(PathObj.join('.'), ObjectData);
    } else if (Array.isArray(ObjectData)) {
      for (let F = 0; F < ObjectData.length; F++) {
        const PathObj = Path != null ? JSON.parse(JSON.stringify(Path)) : [];
        PathObj[PathObj.length - 1] = PathObj[PathObj.length - 1] + '[' + F + ']';
        FormDataBase = this.calculateFormDataObject(ObjectData[F], PathObj, FormDataBase) || FormDataBase;
      }
    } else if (typeof ObjectData === 'function' && ObjectData != null) {
      return null;
    } else if (typeof ObjectData === 'object' && ObjectData != null) {
      for (const Key in ObjectData) {
        if (ObjectData.hasOwnProperty(Key)) {
          const PathObj = Path != null ? JSON.parse(JSON.stringify(Path)) : [];
          PathObj.push(Key);
          FormDataBase = this.calculateFormDataObject(ObjectData[Key], PathObj, FormDataBase) || FormDataBase;
        }
      }
    } else if (ObjectData != null) {
      const PathObj = Path != null ? JSON.parse(JSON.stringify(Path)) : [];
      FormDataBase.append(PathObj.join('.'), ObjectData);
    }
    return FormDataBase;
  },
};

export default apiService;
